import { IControlDefinition } from "Grids/Interfaces";
import { ControlEventTypes } from "Core/Events/EventTypes";
import { TextBlockService, PeoplePickerService } from "Service";
import { IDataRequestOption, IDataRow, IODataResult } from "Entities";
import { Navigation } from "Core";
import { BaseDropDownControl } from "Controls/BaseDropDownControl";

/**
 * ## People Picker
 *
 * Control for selecting people from a directory.
 * @since 4.1.0
 * @example
 * ```JavaScript
 * // Access the instance of a People Picker with ControlID 1337
 * // In script:
 * @@ctr:1337@@
 *
 * // On page:
 * $dms(1337)
 * ```
 */
export class PeoplePickerControl extends BaseDropDownControl {
    /**
     * The css class which should be unique for each control type.
     * @hidden
     */
    protected dmsControlClass = "dms-control-peoplepicker";

    /**
     * @hidden
     */
    protected bindingProperty = "deferredValue";

    /**
     * @hidden
     */
    protected additionalBinding =
        this.controlDefinition.TextColumnName && this.controlDefinition.ValueColumnName != this.controlDefinition.TextColumnName
            ? "listText: " + this.controlDefinition.TextColumnName
            : "";

    private _kendoElement: kendo.ui.ComboBox;

    private data: IODataResult;

    private dataSourceOptions: kendo.data.DataSourceOptions;
    private valueBeforeBinding: string;

    private peoplePickerService: PeoplePickerService;

    /**
     * Create a new PeoplePickerControl.
     * @hidden
     */
    constructor(
        controlDefinition: IControlDefinition,
        textBlockCategory: string,
        textBlockService: TextBlockService,
        peoplePickerService: PeoplePickerService
    ) {
        super(controlDefinition, textBlockCategory, textBlockService);
        this.peoplePickerService = peoplePickerService;
    }

    /**
     * Internal method for setElement.
     * @param element JQueryElement for initialization.
     * @hidden
     */
    protected setElementInternal(element: JQuery): void {
        this.jQueryElement.on("focusout", e => this.trigger(ControlEventTypes.LostFocus));

        let dropDownOptions = this.getDropDownOptions();

        let noDataTemplate = dropDownOptions.noDataTemplate;

        let options: kendo.ui.ComboBoxOptions = {
            dataValueField: "AccountID",
            dataTextField: "DisplayName",
            valuePrimitive: true, // sets the binding to the value field instead of the dataitem itself
            minLength: 3,
            enforceMinLength: true,
            suggest: false,
            filter: "contains",
            autoBind: false,
            change: e => this.onChange(e),
            dataBound: e => this.onDataBound(e),
            noDataTemplate: noDataTemplate,
            autoWidth: this.controlDefinition.AutoWidth || false,
            syncValueAndText: false,
            clearButton: this.controlDefinition.InsertNullValue || false,
            template: `
                <div class="dms-people-item">
                    <span class="dms-people-picture-column"># if (data.get('Picture')) { # <img src="data:image/jpeg;base64,#= data.get('Picture')#" /> # } #</span>
                    <span class="dms-people-text-column"><h3>#=data.get('DisplayName')#</h3><p>#=data.get('Title') || "" #</p></span>
                </div>`
        };

        this.dataSourceOptions = {
            transport: {
                read: (options: kendo.data.DataSourceTransportRead) => {
                    if (this.data) {
                        this.valueBeforeBinding = this._kendoElement.value();
                        (options.success as JQuery.Ajax.SuccessCallback<any>)(this.data, null, null);
                        this.data = undefined;
                        return;
                    }

                    let dataRow = this.execute<IDataRow, {}>(ControlEventTypes.GetDataRow);
                    let requestOptions = this.getDataRequest();
                    requestOptions.SearchValue = null;
                    if (options.data.filter && options.data.filter.filters.length > 0) {
                        let item = <kendo.data.DataSourceFilterItem>options.data.filter.filters[0];
                        requestOptions.SearchText = item.value;
                    }

                    this.peoplePickerService
                        .get(this.controlDefinition.GridID, this.controlDefinition.ControlID, requestOptions, dataRow)
                        .then((result: IODataResult) => {
                            if (result.error) {
                                Navigation.errorAsync(result.error, "Error");
                                return;
                            }

                            (options.success as JQuery.Ajax.SuccessCallback<any>)(result, null, null);
                        });
                }
            },
            schema: {
                data: function(data: IODataResult) {
                    return data.value || [];
                },
                total: function(data: IODataResult) {
                    return data["odata.count"];
                }
            },
            error: e => this.onError(e),
            serverFiltering: true
        };

        this._kendoElement = new kendo.ui.ComboBox(this.jQueryElement[0], options);

        let iconElement = this._kendoElement.wrapper.find(".k-i-arrow-60-down");
        iconElement.removeClass("k-i-arrow-60-down").addClass("fa fa-user");
    }

    /**
     * Gets the outer element. This is either the HTMLElement or a wrapper added by the control.
     * @since 4.1.0
     */
    public getOuterElement(): JQuery {
        return this._kendoElement.wrapper;
    }

    /**
     * Format the value with highlighting from text search.
     * @param value Value to be formatted.
     * @hidden
     */
    public formatValue(value: string): string {
        let text = this._kendoElement.text();
        let idx = this._kendoElement.select();
        if (!text || !value || idx == -1) {
            return value;
        }
        if (this.lastText != text) {
            // todo: refactor replace to own service.
            this.textMatcher = new RegExp(text.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&"), "ig");
            this.lastText = text;
        }
        return value.toString().replace(this.textMatcher, function(match) {
            return "<em>" + match + "</em>";
        });
    }

    /**
     * Occurs after data was bound on the control.
     * @param e EventArgs.
     */
    private onDataBound(e: kendo.ui.ComboBoxDataBoundEvent): void {
        this.setBusy(false);
        // if no element is selected, try to select the first element.
        if (this.valueBeforeBinding !== undefined && this._kendoElement.select() == -1) {
            // if the datasource is empty, we need to clear the text, otherwise the last text is shown
            if (this._kendoElement.dataSource.data().length == 0) {
                this._kendoElement.text(null);
            } else {
                this._kendoElement.select(0);
                if (this.valueBeforeBinding != this._kendoElement.value()) {
                    this._kendoElement.trigger("change");
                }
            }
            this.valueBeforeBinding = undefined;
        }
        this.trigger(ControlEventTypes.DataBound);
    }

    private onError(e: kendo.data.DataSourceErrorEvent): void {
        console.log(e.errorThrown, e.errors);
    }

    /**
     * Set the selected value.
     * @param value Value to be set.
     * @since 4.1.0
     */
    public setValue(value: string): void {
        let oldValue = this.getValue();
        if (oldValue !== value) {
            this._kendoElement.value(value);
            this._kendoElement.trigger("change");
        }
    }

    /**
     * Get the selected value.
     * @since 4.1.0
     */
    public getValue(): string {
        let val = this._kendoElement.value();
        return val == "" ? null : val;
    }

    /**
     * Get the text of the selected item.
     * @since 4.1.0
     */
    public getText(): string {
        return this._kendoElement.text();
    }

    /**
     * Get the index of the selected item.
     * @returns Zero-based index of the selected item.
     * @since 4.1.0
     * @example
     * ```JavaScript
     * @@ctr:1337@@.getSelectedIndex();
     * ```
     */
    public getSelectedIndex(): number {
        return this._kendoElement.select();
    }

    /**
     * Get the kendo combobox control. Click {@link http://docs.telerik.com/kendo-ui/api/javascript/ui/combobox|here} for the api definition.
     * @since 4.1.0
     */
    public getKendoElement(): kendo.ui.ComboBox {
        return this._kendoElement;
    }

    /**
     * Occurs when control value was changed.
     * @param e EventArgs.
     */
    private onChange(e: kendo.ui.ComboBoxEvent) {
        // check if the user entered a text but didnt select a value
        if (this._kendoElement.text() != "" && this._kendoElement.select() === -1) {
            setTimeout(() => {
                this._kendoElement.value(""); //reset widget
            }, 15);
        } else {
            this.trigger(ControlEventTypes.Change, this);
        }
    }

    /**
     * Enable the control.
     * @since 4.1.0
     */
    public enable(): void {
        this._kendoElement.enable(true);
    }

    /**
     * Disable the control.
     * @since 4.1.0
     */
    public disable(): void {
        this._kendoElement.enable(false);
    }

    /**
     * Returns true if the control is enabled.
     * @since 4.1.0
     */
    public isEnabled(): boolean {
        return !this._kendoElement.input.prop("disabled");
    }

    /**
     * Get info for requesting data.
     * @hidden
     */
    public getDataRequest(): IDataRequestOption {
        if (this._kendoElement == null) {
            return null;
        }
        let result: IDataRequestOption = {
            ControlID: this.controlDefinition.ControlID,
            SearchValue: this._kendoElement.value() != "" ? [this._kendoElement.value()] : [],
            Take: null,
            Skip: null,
            FilterOperator: null,
            SearchText: null
        };
        return result;
    }

    /**
     * Set the odata-result to the control.
     * @param data Data to be set.
     * @hidden
     */
    public setData(data: IODataResult): Promise<void> {
        this.data = data;

        this._kendoElement.setDataSource(new kendo.data.DataSource(this.dataSourceOptions));
        this.dataSource = this._kendoElement.dataSource;
        this._kendoElement.dataSource.read();
        return Promise.resolve();
    }

    /**
     * Show loading indicator on the control.
     * @param isBusy True if loading indicator should be shown.
     * @since 4.1.0
     */
    public setBusy(isBusy: boolean): void {
        let element = this.getOuterElement().find(".fa-user");
        if (isBusy) {
            element.addClass("k-i-loading");
        } else {
            element.removeClass("k-i-loading");
        }
    }

    /**
     * Select the item at index.
     * @param index The zero-based index of the item which should be selected.
     * @since 4.1.0
     * @example
     * ```JavaScript
     * // in script:
     * @@ctr:1337@@.selectItemAt(5);
     *
     * // on page:
     * $dms(1337).selectItemAt(5);
     * ```
     */
    public selectItemAt(index: number): void {
        this._kendoElement.select(index);
        this._kendoElement.trigger("change");
    }

    /**
     * Returns true if an item with given value is in the dropdown list.
     * @param value The value to be searched.
     * @since 4.1.0
     * @returns True if the value is in the list of available values.
     * @example
     * ```JavaScript
     * @@ctr:1337@@.containsValue("1");
     * ```
     */
    public containsValue(value: string): boolean {
        this._kendoElement.dataSource.fetch(() => {
            let list = this._kendoElement.dataSource.data();
            let columnName = this.controlDefinition.CommandValueColumnName;
            for (let i = 0, len = list.length; i < len; i++) {
                if (list[i][columnName] == value) {
                    return true;
                }
            }
        });
        return false;
    }

    /**
     * Select the item which text contains given search string.
     * @param text The text to be searched.
     * @since 4.1.0
     */
    public selectItemWithText(text: string): void {
        if (text == null) {
            this.selectItemAt(-1);
            return;
        }
        this._kendoElement.dataSource.fetch(() => {
            let textColumnName = this.controlDefinition.CommandTextColumnName;

            this._kendoElement.select((dataItem: IDataRow) => {
                return dataItem[textColumnName] && dataItem[textColumnName].toString().indexOf(text) !== -1;
            });
            this._kendoElement.trigger("change");
        });
    }

    /**
     * Reloads the data from the datasource and selects the value after the loading.
     * This is useful when you create a entry in an additional form and want to select the entry after saving the form.
     * @param value The value to be selected after reloading.
     * @since 4.1.0
     */
    public reloadAndSetValue(value: string): Promise<void> {
        this._kendoElement.dataSource.data([
            { [this._kendoElement.options.dataValueField]: value, [this._kendoElement.options.dataTextField]: "" }
        ]);
        this._kendoElement.value(value);
        return this.reload();
    }

    /**
     * Reload the control items with data from the server.
     * @since 4.1.0
     */
    public reload(): Promise<void> {
        return this.execute<Promise<void>>(ControlEventTypes.ControlReloadRequested);
    }

    /**
     * Set the search filter for the combobox.
     * @param text Text to be searched.
     * @since 4.0.2
     */
    public setSearch(text: string): void {
        this._kendoElement.search(text);
    }

    /**
     * Dispose the control and release all resources.
     * @since 4.1.0
     */
    public dispose(): void {
        super.dispose();
        if (this._kendoElement) {
            this._kendoElement.destroy();
        }
        if (this.jQueryElement) {
            this.jQueryElement.off("focusout");
        }
    }
}
